import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiRequest, clearTokens, getAccessToken, setTokens } from '@/api/client'
import type { AuthResponse, MeResponse } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const me = ref<MeResponse | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => me.value !== null)
  const isAdmin = computed(() => me.value?.role === 'admin')

  async function login(email: string, password: string): Promise<void> {
    const res = await apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      noAuth: true,
    })
    setTokens(res.access_token, res.refresh_token)
    await loadMe()
  }

  async function loadMe(): Promise<void> {
    loading.value = true
    try {
      me.value = await apiRequest<MeResponse>('/me')
    } finally {
      loading.value = false
    }
  }

  // Called once on app boot to resolve auth state from a stored token.
  async function initialize(): Promise<void> {
    if (initialized.value) return
    initialized.value = true
    if (!getAccessToken()) return
    try {
      await loadMe()
    } catch {
      // Token was invalid and the refresh failed; treat as logged out.
      clearTokens()
      me.value = null
    }
  }

  function logout(): void {
    clearTokens()
    me.value = null
  }

  return { me, loading, isAuthenticated, isAdmin, login, loadMe, initialize, logout }
})
